/**
 * 🔴 IL TEMPO REALE DI QSOCIALS — un collegamento solo per app a `qsocials-ws` (Centrifugo v5), una
 * sottoscrizione sola: il canale personale `presence:friends:<sub>`.
 *
 * L'interfaccia NON e' nostra: sta nel contratto di fronte `fronti/qsocials/tempo-reale-e-dm.md` §1-bis
 * (KB). Nomi dei canali, forma degli eventi, emittente dei token si leggono li', non si inventano qui.
 *
 * La regola che rende corretto il tutto (§1-bis): il namespace `presence` NON ha storia, quindi un evento
 * pubblicato mentre eravamo scollegati e' PERSO. ⇒ **a ogni (ri)sottoscrizione riuscita si rileggono le
 * liste**: l'evento accelera, la rilettura garantisce.
 *
 * Scelte dichiarate (cantiere `tma-orcb-1-qsocials`, 2026-09-25):
 *  - libreria `centrifuge` (npm, v5) e NON il modello di `useChat.ts`: quello parla il protocollo vecchio
 *    (`result.data.data`), non manda token di sottoscrizione e non rinnova niente. I token qui SCADONO
 *    (connessione 24 h, sottoscrizione 5 min) e il rinnovo e' `getToken` del protocollo v5: la libreria
 *    chiede un token nuovo prima della scadenza e lo manda con `refresh` / `sub_refresh`.
 *  - `centrifuge` si carica con un IMPORT DINAMICO (`./collegamento`), solo con l'interruttore acceso: a
 *    interruttore vuoto (oggi la produzione) il bundle non paga i ~20 kB gzip della libreria. Questo file
 *    resta puro apposta: lo importano anche `Friends.tsx` e il hook. `tools/verify-tempo-reale.ts` lo vieta.
 *  - un errore del collegamento NON arriva mai a schermo (R4): cambia solo lo stato, e con lo stato
 *    diverso da `vivo` la pagina Amici torna al ricontrollo periodico.
 */

/** `spento` = interruttore vuoto o nessun utente · `collegando` · `vivo` = sottoscritto · `giu`. */
export type StatoTempoReale = 'spento' | 'collegando' | 'vivo' | 'giu';

/** I tag RTK che il tempo reale sa invalidare. `Dm*` li fornira' il codice DM (oggi nessuno: no-op). */
export type TagTempoReale =
  | 'Friends'
  | 'FriendRequests'
  | 'DmConversations'
  | 'DmUnread'
  | { type: 'UserProfile'; id: string };

/** Cosa si rilegge a OGNI sottoscrizione riuscita, la prima e ogni ri-sottoscrizione (§1-bis). */
export const TAG_ALLA_SOTTOSCRIZIONE: readonly TagTempoReale[] = [
  'Friends',
  'FriendRequests',
  'DmConversations',
  'DmUnread',
];

const idUtente = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);

/**
 * Da un evento del canale personale (il `data` della publication) ai tag da rileggere.
 * Gli eventi non portano nomi ne' testi: si rilegge la lista giusta e si mostra cio' che l'API dice.
 * Un `type` sconosciuto (anche `presence`, che esiste ma non e' di questo lavoro) ⇒ nessun tag.
 */
export function tagPerEvento(data: unknown): TagTempoReale[] {
  if (data === null || typeof data !== 'object') return [];
  const e = data as Record<string, unknown>;
  const profilo = (v: unknown): TagTempoReale[] => {
    const id = idUtente(v);
    return id ? [{ type: 'UserProfile', id }] : [];
  };
  switch (e.type) {
    case 'friend.request':
      return ['FriendRequests', ...profilo(e.fromUserId)];
    case 'friend.accepted':
      return ['Friends', 'FriendRequests', ...profilo(e.byUserId)];
    // `dm.erased`: l'altro ha cancellato i suoi messaggi (P1), cambiano anteprima e non letti.
    case 'dm.message':
    case 'dm.erased':
      return ['DmConversations', 'DmUnread'];
    default:
      return [];
  }
}

/**
 * Il ricontrollo periodico della pagina Amici (si ferma con la pagina nascosta: `skipPollingIfUnfocused`).
 *  - interruttore vuoto ⇒ 0: la TMA si comporta come prima (solo `refetchOnFocus`);
 *  - collegamento `vivo` ⇒ ogni 60 s: e' la RETE DI SICUREZZA di R7 (contratto accettato @ cf0b5d97). Un
 *    publish puo' fallire mentre il client resta collegato, e allora non arriva ne' l'evento ne' una
 *    risottoscrizione: la rilettura fa arrivare lo stato giusto entro un minuto. La prova di R3 non dipende
 *    piu' dall'assenza di polling ma dall'header `X-Request-Id: ev-…` (`idEvento.ts`);
 *  - interruttore acceso ma collegamento non vivo (in corso, caduto, rifiutato) ⇒ ogni 30 s (R4).
 */
export const RETE_DI_SICUREZZA_MS = 60_000;
export const RICONTROLLO_AMICI_MS = 30_000;
export function ricontrolloAmiciMs(url: string, stato: StatoTempoReale): number {
  if (!url) return 0;
  return stato === 'vivo' ? RETE_DI_SICUREZZA_MS : RICONTROLLO_AMICI_MS;
}

/**
 * «Subito al rientro dall'app in secondo piano» (R7). La TMA segue il rientro con `visibilitychange`
 * (`useIsVisible`, `ResumableMatchContext`, `setupListeners` di RTK); `@tma.js` non espone un evento
 * `activated` (cercato nel `node_modules`, 25/09). Si ascolta anche `focus`, come fa RTK.
 * ⚠️ In secondo piano la WebView puo' sospendere i timer: li' la promessa e' «subito al rientro».
 */
export function ascoltaRientro(alRientro: () => void): () => void {
  const suVisibilita = () => {
    if (document.visibilityState === 'visible') alRientro();
  };
  document.addEventListener('visibilitychange', suVisibilita);
  window.addEventListener('focus', alRientro);
  return () => {
    document.removeEventListener('visibilitychange', suVisibilita);
    window.removeEventListener('focus', alRientro);
  };
}

/**
 * Un `unsubscribed` DEFINITIVO: il token rifiutato da noi (codice 1, `unauthorized` della libreria) o un
 * errore permanente del server sulla sottoscrizione (codice >= 100, per esempio 103 «permission denied»).
 * NON lo sono: 0 (lo abbiamo chiesto noi), 2 (client chiuso), >= 2500 (il server chiede di risottoscrivere).
 * Con un rifiuto definitivo il socket non serve piu' a niente: si chiude (v. `collegamento.ts`).
 */
export function rifiutoDefinitivo(codice: number): boolean {
  return codice === 1 || (codice >= 100 && codice < 2500);
}

/** Il canale personale: il nome e' storico, e' il canale del proprietario (§1-bis). */
export const canalePersonale = (sub: string): string => `presence:friends:${sub}`;

export interface AvvioTempoReale {
  /** `VITE_QSOCIALS_WS_URL`. **Vuoto ⇒ nessun collegamento**: e' l'interruttore. */
  url: string;
  /** QSocials acceso in questa build (`VITE_FEATURE_SOCIAL_ENABLED`). */
  socialAcceso: boolean;
  /** Il `sub` Logto dell'utente, o null se non c'e' una sessione. */
  sub: string | null;
  /** `GET /social/ws-token` (senza canale) o `?channel=` (con): restituisce il token nuovo. */
  chiediToken: (canale?: string) => Promise<string>;
  onSottoscritto: () => void;
  onEvento: (data: unknown) => void;
  onStato: (stato: StatoTempoReale) => void;
  /** Solo per i test: il costruttore del WebSocket (il finto Centrifugo). */
  websocket?: unknown;
}

/**
 * Un rifiuto DEFINITIVO del token (niente sessione, canale non nostro): la libreria smette di riprovare.
 * E' nostro e non `UnauthorizedError` di `centrifuge` per non importare la libreria da qui: la traduzione
 * sta in `./collegamento`, che la libreria la carica.
 */
export class TokenRifiutato extends Error {
  constructor(motivo: string) {
    super(motivo);
    this.name = 'TokenRifiutato';
  }
}

/**
 * Apre il collegamento, o non fa NIENTE se l'interruttore e' vuoto / QSocials spento / nessun utente —
 * e in quel caso non carica nemmeno la libreria.
 * Restituisce la funzione che lo chiude, o `null` se non ha aperto nulla.
 */
export function avviaTempoReale(o: AvvioTempoReale): (() => void) | null {
  if (!o.url || !o.socialAcceso || !o.sub) {
    o.onStato('spento');
    return null;
  }
  const canale = canalePersonale(o.sub);
  let chiuso = false;
  let chiudi: (() => void) | null = null;
  o.onStato('collegando');
  import('./collegamento')
    .then(({ apriCollegamento }) => {
      if (chiuso) return;
      const aperto = apriCollegamento(o, canale);
      chiudi = aperto.chiudi;
      sottoscrittore = aperto.sottoscrivi;
    })
    // Il pezzo non si e' caricato (rete, deploy nuovo): niente collegamento, resta il ripiego (R4).
    .catch(() => {
      if (!chiuso) o.onStato('giu');
    });
  return () => {
    chiuso = true;
    sottoscrittore = null;
    chiudi?.();
    o.onStato('spento');
  };
}

/** Cosa riceve chi apre un canale in piu' (oggi: la conversazione aperta, `dm:<conversationId>`). */
export interface GestoriCanale {
  onDato: (data: unknown) => void;
  /** Ogni sottoscrizione riuscita: si rilegge dall'API (niente recupero dalla storia, §1-bis). */
  onSottoscritto?: () => void;
  /** Rifiuto DEFINITIVO del canale (per esempio non siete piu' amici): NON chiude il collegamento. */
  onRifiuto?: () => void;
}
let sottoscrittore: ((canale: string, g: GestoriCanale) => () => void) | null = null;

/**
 * Il canale della conversazione aperta, sullo STESSO collegamento (uno per app). Senza collegamento
 * (interruttore vuoto, libreria non ancora caricata) non fa niente: vale la rilettura periodica (R7), e chi
 * chiama riprova quando lo stato torna `vivo`.
 */
export function apriCanaleConversazione(conversationId: string, g: GestoriCanale): () => void {
  return sottoscrittore ? sottoscrittore(`dm:${conversationId}`, g) : () => {};
}
